import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-elevation-2">
        <p className="text-sm font-medium text-foreground mb-2">{label}</p>
        <p className="text-xs text-muted-foreground">Accuracy: {d.accuracy}%</p>
        <p className="text-xs text-muted-foreground">Surveys: {d.surveys}</p>
      </div>
    );
  }
  return null;
};

const getAccuracyColor = (accuracy) => {
  if (accuracy >= 95) return 'var(--color-success)';
  if (accuracy >= 90) return 'var(--color-warning)';
  return 'var(--color-error)';
};

const ValuationAccuracyMetrics = ({ data = [], className = '' }) => {
  const chartData = data.map(s => ({ surveyor: s.name, accuracy: s.accuracy, surveys: s.surveys }));
  const teamAvg = data.length
    ? (data.reduce((sum, s) => sum + s.accuracy, 0) / data.length).toFixed(1)
    : 0;
  const totalSurveys = data.reduce((sum, s) => sum + s.surveys, 0);

  return (
    <div className={`bg-card rounded-lg border border-border p-6 ${className}`}>
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Target" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-foreground">Surveyor Performance</h3>
      </div>

      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-80 text-muted-foreground text-sm">No data available</div>
      ) : (
        <>
          <div className="w-full h-80 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="surveyor" stroke="var(--color-muted-foreground)" fontSize={12} angle={-45} textAnchor="end" height={80} />
                <YAxis stroke="var(--color-muted-foreground)" fontSize={12} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="accuracy" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground mb-3">Surveyor Performance Details</h4>
            {data.map((surveyor, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-md">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: getAccuracyColor(surveyor.accuracy) }} />
                  <div>
                    <p className="text-sm font-medium text-foreground">{surveyor.name}</p>
                    <p className="text-xs text-muted-foreground">{surveyor.surveys} surveys</p>
                  </div>
                </div>
                <p className="text-sm font-semibold text-foreground">{surveyor.accuracy}%</p>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-border grid grid-cols-2 gap-4 text-center">
            <div className="p-3 bg-muted rounded-md">
              <p className="text-lg font-bold text-success">{teamAvg}%</p>
              <p className="text-xs text-muted-foreground">Team Average</p>
            </div>
            <div className="p-3 bg-muted rounded-md">
              <p className="text-lg font-bold text-primary">{totalSurveys}</p>
              <p className="text-xs text-muted-foreground">Total Surveys</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ValuationAccuracyMetrics;
